import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { upworkAction } from '../services/upworkActionService';
export default function UpworkPrepareProposal({ onChanged = () => {} }) {
  const { id } = useParams();
  const { hasPermission, canApproveUpworkActions } = usePermissions();
  const [enabled, setEnabled] = useState(false);
  const [writesEnabled, setWritesEnabled] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [orgUid, setOrgUid] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [prepared, setPrepared] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setEnabled(false); setPrepared(null); setConsent(false);
    upworkAction('/status').then(async (s) => {
      if (cancelled) return;
      const request = await upworkAction(`/proposal/${id}`);
      if (!s.enabled) {
        if (!cancelled) { setPrepared(request); setEnabled(Boolean(request)); setWritesEnabled(false); }
        return;
      }
      const c = await upworkAction('/accounts');
      if (cancelled) return;
      const choices = (c.accounts || []).filter((a) => ['TALENT', 'FL_AGENCY'].includes(a.role));
      setPrepared(request); setAccounts(choices); setOrgUid(c.defaultOrgUid || (choices.length === 1 ? choices[0].orgUid : '')); setWritesEnabled(s.writesEnabled); setEnabled(Boolean(request) || s.writesEnabled);
    }).catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [id]);
  if (!enabled || !hasPermission('job:apply')) return null;
  const prepare = async () => {
    setBusy(true); setError('');
    try { setPrepared(await upworkAction('/prepare', 'POST', { proposalId: id, orgUid, consentToSend: consent })); onChanged(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true); setError('');
    try { await upworkAction(`/${prepared._id}/cancel`, 'POST'); setPrepared(null); setConsent(false); onChanged(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  return <section className="mx-auto max-w-4xl border rounded-md p-4 my-4 space-y-3">
    <h2 className="font-semibold">Prepare on Upwork</h2>
    {!writesEnabled && <p>New Upwork submissions are disabled. You can still cancel an undispatched review.</p>}
    {prepared ? <div className="space-y-2"><p role="status">{prepared.status === 'pending' ? 'Ready for human review.' : `Upwork request: ${prepared.status.replaceAll('_', ' ')}.`} {canApproveUpworkActions() && <Link className="text-primary-700 underline" to="/admin/approvals">Open approvals</Link>}</p>
      {['preparing', 'pending', 'approved'].includes(prepared.status) && <button className="rounded-md border py-1.5 px-3 text-sm" disabled={busy} onClick={cancel}>Cancel review to edit</button>}
      {['executing', 'outcome_unknown'].includes(prepared.status) && <p>Check the result on Upwork. Do not create another submission while the outcome is unresolved.</p>}
    </div> : <>
      <p className="text-sm">Uses the saved draft. Preparation does not submit it. Add files or highlights through the existing Upwork flow until attachment support is available.</p>
      <label className="block text-sm">Submitting account<select className="block border rounded-md py-2 px-3" value={orgUid} onChange={(e) => { setOrgUid(e.target.value); setConsent(false); }}><option value="">Choose an account</option>{accounts.map((a) => <option key={a.orgUid} value={a.orgUid}>{a.name} · {a.roleLabel || a.role}</option>)}</select></label>
      <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />A permitted reviewer may send this exact saved draft through my selected connection after reviewing its preview.</label>
      <button className="rounded-md bg-primary-600 text-white py-1.5 px-3 text-sm disabled:opacity-50" disabled={busy || !writesEnabled || !consent || !orgUid} onClick={prepare}>{busy ? 'Preparing…' : 'Prepare for review'}</button>
    </>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
  </section>;
}
